<template>
  <div 
    ref="coverageContainer"
    class="coverage-comparison"
    :class="{ 'loading': !isInViewport }"
  >
    <div v-if="!isInViewport" class="chart-placeholder">
      <div class="placeholder-content">
        <div class="placeholder-icon">🚇</div>
        <div class="placeholder-text">覆盖率对比图表加载中...</div>
      </div>
    </div>
    <div v-else class="comparison-content">
      <div class="coverage-charts">
        <div class="coverage-item">
          <h4>中心城区600米覆盖率</h4>
          <div class="coverage-bars">
            <div class="bar-container">
              <div class="bar-label">现状</div>
              <div class="bar-wrapper">
                <div class="bar current" :style="{ width: '45%' }">
                  <span class="bar-value">45%</span>
                </div>
              </div>
            </div>
            <div class="bar-container">
              <div class="bar-label">2035年目标</div>
              <div class="bar-wrapper">
                <div class="bar target" :style="{ width: '60%' }">
                  <span class="bar-value">60%+</span>
                </div>
              </div>
            </div>
            <div class="improvement">
              <i class="fas fa-arrow-up"></i>
              <span>提升15个百分点</span>
            </div>
          </div>
        </div>

        <div class="coverage-item">
          <h4>新城轨道交通覆盖率</h4>
          <div class="coverage-bars">
            <div class="bar-container">
              <div class="bar-label">现状</div>
              <div class="bar-wrapper">
                <div class="bar current" :style="{ width: '20%' }">
                  <span class="bar-value">20%</span>
                </div>
              </div>
            </div>
            <div class="bar-container">
              <div class="bar-label">2035年目标</div>
              <div class="bar-wrapper">
                <div class="bar target" :style="{ width: '40%' }">
                  <span class="bar-value">40%</span>
                </div>
              </div>
            </div>
            <div class="improvement">
              <i class="fas fa-arrow-up"></i>
              <span>提升20个百分点</span>
            </div>
          </div>
        </div>
      </div>

      <div class="planning-details">
        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-city"></i>
            <h5>中心城区覆盖目标</h5>
          </div>
          <div class="card-content">
            <div class="strategy-item">
              <strong>目标要求：</strong>
              <span>到2035年，中心城区轨道交通站点600米覆盖率达到60%以上（当前约为45%）</span>
            </div>
            <div class="strategy-item">
              <strong>重点加密区域：</strong>
              <span>浦东中部、虹口北部等区域的覆盖盲区</span>
            </div>
            <div class="strategy-item">
              <strong>新增线路：</strong>
              <span>20号线二期、23号线西延伸等将重点填补覆盖空白</span>
            </div>
            <div class="strategy-item">
              <strong>实现手段：</strong>
              <span>通过新增市区线（地铁）填补中心城覆盖空白</span>
            </div>
          </div>
        </div>

        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-building"></i>
            <h5>新城覆盖目标</h5>
          </div>
          <div class="card-content">
            <div class="strategy-item">
              <strong>目标要求：</strong>
              <span>所有新城轨道交通站点覆盖率提升至不低于40%（当前部分新城不足20%）</span>
            </div>
            <div class="strategy-item">
              <strong>建设要求：</strong>
              <span>规划要求每个新城至少布局1条局域线，强化内部覆盖</span>
            </div>
            <div class="strategy-item">
              <strong>重点项目：</strong>
              <span>松江T2线延伸、青浦新城线等局域线建设</span>
            </div>
            <div class="strategy-item">
              <strong>目标导向：</strong>
              <span>通过局域线（中运量轨道）提升新城内部可达性，构建多层级覆盖网络</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const coverageContainer = ref<HTMLDivElement | null>(null);
let intersectionObserver: IntersectionObserver | null = null;
const isInViewport = ref(false);
const hasAnimated = ref(false);

interface ScaleItem {
  title: string
  value: string
  description: string
  color: string
}

const scaleData: ScaleItem[] = [
  {
    title: '现有网络',
    value: '~650公里',
    description: '已建成运营线路基础',
    color: '#10b981'
  },
  {
    title: '在建线路',
    value: '~150公里', 
    description: '13号线西延伸、19号线等将于2025-2027年陆续开通',
    color: '#f59e0b'
  },
  {
    title: '规划新增',
    value: '22条新线',
    description: '新增22条新线，总里程冲刺1000公里，含"史上最长大环线"26号线',
    color: '#6366f1'
  },
  {
    title: '2035年总目标',
    value: '约2200公里',
    description: '形成轨道交通网络（含既有线、在建线及规划新增线路）',
    color: '#0085C3'
  }
]

// 生命周期钩子
onMounted(() => {
  if (coverageContainer.value) {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.value) {
            isInViewport.value = true;
            hasAnimated.value = true;
            // 添加动画类
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '50px'
      }
    );
    
    intersectionObserver.observe(coverageContainer.value);
  }
});

onBeforeUnmount(() => {
  if (intersectionObserver) {
    intersectionObserver.disconnect();
  }
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;500;600;700&display=swap');

.coverage-comparison {
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 12px;
  font-family: 'Noto Serif SC', serif;
  position: relative;
  min-height: 400px;
}

.coverage-comparison.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.placeholder-content {
  text-align: center;
  color: #6b7280;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.7;
  animation: pulse 2s infinite;
}

.placeholder-text {
  font-size: 1.1rem;
  font-weight: 500;
  opacity: 0.8;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

.comparison-header {
  text-align: center;
  margin-bottom: 24px;
}

.comparison-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  font-family: 'Noto Serif SC', serif;
}

.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-family: 'Noto Serif SC', serif;
}

.comparison-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.coverage-charts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.coverage-item {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.coverage-item h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  text-align: center;
  font-family: 'Noto Serif SC', serif;
}

.coverage-bars {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bar-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bar-label {
  width: 80px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  font-family: 'Noto Serif SC', serif;
}

.bar-wrapper {
  flex: 1;
  height: 32px;
  background: #f3f4f6;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

.bar {
  height: 100%;
  border-radius: 16px;
  position: relative;
  transition: width 1s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bar.current {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.bar.target {
  background: linear-gradient(90deg, #10b981, #059669);
}

.bar-value {
  color: white;
  font-size: 13px;
  font-weight: 600;
}

.improvement {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #059669;
  font-weight: 600;
  margin-top: 8px;
  justify-content: center;
}

.improvement i {
  font-size: 16px;
}

.planning-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.detail-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  background: linear-gradient(135deg, #0085C3, #006494);
  color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-header i {
  font-size: 20px;
}

.card-header h5 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  font-family: 'Noto Serif SC', serif;
}

.card-content {
  padding: 20px;
}

.strategy-item {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.5;
}

.strategy-item strong {
  color: #374151;
  min-width: 80px;
  font-family: 'Noto Serif SC', serif;
}

.strategy-item span {
  color: #6b7280;
  flex: 1;
  font-family: 'Noto Serif SC', serif;
}

.network-scale {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.scale-header h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  text-align: center;
  border-bottom: 2px solid #0085C3;
  padding-bottom: 10px;
  font-family: 'Noto Serif SC', serif;
}

.scale-timeline {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  background: #f8fafc;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.timeline-item:hover {
  border-color: #0085C3;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}

.timeline-content {
  flex: 1;
}

.timeline-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  font-family: 'Noto Serif SC', serif;
}

.timeline-value {
  font-size: 16px;
  font-weight: 700;
  color: #0085C3;
  margin-bottom: 4px;
}

.timeline-desc {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  font-family: 'Noto Serif SC', serif;
}

.planning-summary {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-header h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  text-align: center;
  border-bottom: 2px solid #0085C3;
  padding-bottom: 10px;
  font-family: 'Noto Serif SC', serif;
}

.summary-content p {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  margin-bottom: 16px;
  text-align: justify;
  font-family: 'Noto Serif SC', serif;
}

.summary-content p:last-child {
  margin-bottom: 0;
}

.summary-content strong {
  color: #0085C3;
  font-weight: 600;
  font-family: 'Noto Serif SC', serif;
}

/* 滚动条样式 */
.coverage-comparison::-webkit-scrollbar {
  width: 6px;
}

.coverage-comparison::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.coverage-comparison::-webkit-scrollbar-thumb {
  background: #0085C3;
  border-radius: 3px;
}
</style>